import {
	AlertDialog,
	Dialog,
	Form,
	FormattedText,
	FormItem,
	Icon,
	TextArea,
	Toolbar,
	ValidationError,
	ButtonBase
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import { useDataInputDialog } from "client/Hooks";
import { isNotNilOrEmpty } from "client/Misc/utils";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind
} from "client/Validation";
import {
	doesNotExceedLength,
	isNotEmpty,
	isNotNullNorUndefined
} from "client/Validation/rules";
import { localize } from "client_legacy/ndaw/culture";
import { userConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import {
	ReactElement,
	useCallback,
	useMemo,
	useState
} from "react";
import { EMBED_CODE_MAX_LENGTH } from "../Builder/validation";
import styles from "./EmbedInputDialog.module.less";

export type EmbedInputDialogProps = {
	code: string;
	onChange(code: string): void;
	onClose(): void;
};

export const EmbedInputDialog = (props: EmbedInputDialogProps): ReactElement => {
	const {
		code,
		onChange,
		onClose
	} = props;

	const [formData, setFormData] = useState<FormData>({ code });
	const [isWarningVisible, setIsWarningVisible] = useState<boolean>(false);

	const formHandler: IFormHandler<FormData> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const productName = useMemo(
		(): string => {
			return userConfiguration()
				.branding
				.productName;
		},
		[]
	);

	const submitHandler = useCallback(
		async (): Promise<void> => {
			const validationResult: InputValidationResult = await formHandler.validation
				.validate();

			if (validationResult.kind === InputValidationResultKind.Invalid) {
				return;
			}

			onChange(formData.code);
		},
		[
			formData,
			formHandler,
			onChange
		]
	);

	return (
		<Dialog trackingId="embed-input-dialog">
			<Dialog.Header
				title={localize(
					isNotNilOrEmpty(code)
						? "_AddEmbed"
						: "_EditEmbed"
					)}
				onClose={onClose}
			/>
			<Dialog.Body>
				<Form onSubmit={submitHandler}>
					<FormItem label={localize("_EmbedCode")}>
						<div className={styles.embedInputWrapper}>
							<TextArea
								{...formHandler.for("code")
									.attachToTextArea()
								}
							/>
							<ButtonBase
								className={styles.embedInputWarningButton}
								onClick={() => setIsWarningVisible(true)}
								trackingId="embed-input-warning-button"
							>
								<Icon value="triangle-exclamation" />
							</ButtonBase>
							{isWarningVisible && (
								<AlertDialog
									title={localize("_EmbedCode")}
									onClose={() => setIsWarningVisible(false)}
									confirmText={localize("_Close")}
									trackingId="embed-code-warning-dialog"
								>
									<FormattedText
										text={localize("_EmbedCodeRisksStatement")}
										thingsToReplace={[
											{
												what: "{{ProductName}}",
												filler: productName
											}
										]}
									/>
								</AlertDialog>
							)}
							<ValidationError
								{...formHandler.for("code")
									.attachToValidationError()
								}
							/>
						</div>
					</FormItem>
				</Form>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						text={localize(
							isNotNilOrEmpty(code)
								? "_Save"
								: "_Add"
						)}
						kind="primary"
						onClick={submitHandler}
						trackingId="save-button"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
EmbedInputDialog.displayName = "EmbedInputDialog";

type FormData = {
	code: string;
};

const validationConfiguration = createValidationConfiguration<FormData>();

validationConfiguration.forScalar("code")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(EMBED_CODE_MAX_LENGTH)
	);

export type EmbedInputDialogData = Omit<EmbedInputDialogProps, "onChange" | "onClose">;

export function useEmbedInputDialog(
): [
	element: ReactElement | null,
	open: (data: EmbedInputDialogData) => Promise<TryResult<string>>
] {
	return useDataInputDialog<EmbedInputDialogData, string>(
		(openData, onClose, onSave) => {
			return (
				<EmbedInputDialog
					{...openData}
					onClose={onClose}
					onChange={onSave}
				/>
			);
		}
	);
}
